import React from "react";
import { useOutletContext } from "react-router-dom";
import { CheckCircle2, Heart, Target, Download } from "lucide-react";
import { personSchema, breadcrumbSchema } from "@/components/Seo";
import PageSeo from "@/components/PageSeo";
import PageHeader from "@/components/public/PageHeader";
import { Reveal } from "@/components/public/Primitives";
import { useLanguage } from "@/context/LanguageContext";

export default function About() {
  const { settings } = useOutletContext();
  const { tr } = useLanguage();

  const values = [
    { icon: Heart, title: "Accessibility", desc: "Always reachable for citizens who need help." },
    { icon: CheckCircle2, title: "Accountability", desc: "Honest follow-up until a problem is solved." },
    { icon: Target, title: "Service", desc: "People-first action over politics and position." },
  ];

  const blocks = [
    { title: "Personal Introduction", body: settings?.aboutIntro },
    { title: "Social-Service Journey", body: settings?.aboutJourney },
    { title: "Leadership Philosophy", body: settings?.aboutPhilosophy },
    { title: "Vision for Civic Leadership", body: settings?.aboutVision },
  ];

  return (
    <div data-testid="about-page">
      <PageSeo
        slug="about"
        title="About Girish Kumar Naidu | Social Service Journey"
        description="Learn about Girish Kumar Naidu's social-service journey, charitable trust work, leadership philosophy, and vision for civic leadership in Hoodi Ward 29."
        keywords="Girish Kumar Naidu, about, social service, biography, charitable trust, Bengaluru"
        image={settings?.heroImage}
        schemas={[
          { "@context": "https://schema.org", "@type": "ProfilePage", mainEntity: personSchema(settings) },
          breadcrumbSchema([{ name: "Home", url: window.location.origin }, { name: "About" }]),
        ]}
      />
      <PageHeader
        overline={tr("page.about.overline")}
        title={tr("page.about.title")}
        subtitle={tr("page.about.subtitle")}
        crumbs={[{ name: "About" }]}
      />

      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative sticky top-28">
                <div className="absolute -top-4 -right-4 bottom-4 left-4 bg-navy rounded-3xl" aria-hidden="true" />
                <div className="relative rounded-3xl overflow-hidden aspect-[4/5] border border-gray-200 shadow-2xl">
                  <img src={settings?.heroImage} alt="Girish Kumar Naidu" className="h-full w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-12">
            {blocks.map((b, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <p className="font-mono text-xs text-gold font-bold mb-3">{String(i + 1).padStart(2, "0")}</p>
                <h2 className="font-display text-2xl md:text-3xl font-black text-navy mb-3 tracking-tight">{b.title}</h2>
                <p className="text-gray-600 leading-relaxed">{b.body}</p>
              </Reveal>
            ))}
            <Reveal>
              <button
                data-testid="download-profile"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-navy text-white font-bold hover:bg-navy-700 transition-colors"
                onClick={() => window.print()}
              >
                <Download className="h-4 w-4" /> Download Profile
              </button>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-paper py-16 md:py-24 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-3 gap-6">
            {values.map((v, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 border border-gray-200 card-lift h-full">
                  <div className="h-12 w-12 rounded-xl bg-navy flex items-center justify-center mb-5">
                    <v.icon className="h-6 w-6 text-gold" />
                  </div>
                  <h3 className="font-display text-xl font-black text-navy mb-2">{v.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
